import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { formatAssetValue } from '@/_pages/assets/asset/info/_pageModule/helpers';
import { CurrencyValue } from '@/_pages/assets/asset/info/_pageModule/types';
import { isPrivateDocumentDto, useDocuments } from '@/entities/document';
import { renderSignatories } from '@/entities/privateDocuments/helpers/renderSignatories';
import { useI18nValues as usePrivateDocValues } from '@/entities/privateDocuments/hooks/useI18nValues';
import { AssetDto, useSJDatabase } from '@/shared/lib/sj-orm';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';
import {
  EntityPdfData,
  EntityPdfField,
  EntityPdfSection,
} from '@/shared/pdf-templates/EntityPDF/types';
import { dates } from '@/shared/utils/dates';
import { files } from '@/shared/utils/files';

import { AssetFieldValue } from '../components/AssetFieldValue';
import { ASSET_CURRENCY_FIELDS, ASSET_DOCUMENT_FIELDS, AssetsForms } from '../helpers';
import { createAssetFieldFilter } from '../helpers/createAssetFieldFilter';

import { useAssetDocuments } from './useAssetDocuments';
import { useAttributeGroups } from './useAttributeGroups';
import { useCoOwners } from './useCoOwners';
import { useFetchAssetPhotos } from './useFetchAssetPhotos';
import { FormConfig } from './useFormFields';

const EMPTY_TEXT = '-';

export const useAssetPdfData = () => {
  const { t } = useTranslation('forms_assets');
  const privateDocValues = usePrivateDocValues();
  const groups = useAttributeGroups();
  const { getDocument } = useDocuments();
  const { getPhotos } = useFetchAssetPhotos();
  const { getAssetDocuments } = useAssetDocuments();
  const { getCoOwnerName, getCoOwnerDocument } = useCoOwners(true);
  const sjDb = useSJDatabase();

  return useMemo(() => {
    const getEntityData = async (asset: AssetDto): Promise<EntityPdfData> => {
      const formsArray = AssetsForms[asset.category] ?? [];
      const allPhotos = await getPhotos(asset.attachments ?? []);
      const photos = await Promise.all(allPhotos.map((photo) => files.toBase64(photo.file!)));
      const attachments = photos.filter((photo) => photo !== null);
      const { all: assetDocuments } = getAssetDocuments(asset);

      const sections: EntityPdfSection[] = formsArray.slice(0, -1).map((sectionItems, index) => {
        const filter = createAssetFieldFilter({
          asset,
          formData: formsArray[index] as FormConfig,
          excludedFields: ['attachments'],
        });

        const fields: EntityPdfField[] = Object.entries(sectionItems)
          .filter(([key]) => filter(key as keyof AssetDto))
          .map(([fieldKey, fieldValue]) => {
            const type = fieldValue.type as QuestionType;
            const label = t(fieldValue.label);

            if (ASSET_CURRENCY_FIELDS.includes(type)) {
              const assetValue = asset?.[fieldKey as keyof AssetDto] as CurrencyValue | undefined;
              return { label, value: assetValue ? formatAssetValue(assetValue) : EMPTY_TEXT };
            }

            if (ASSET_DOCUMENT_FIELDS.includes(fieldKey)) {
              const value = fieldValue.value;
              const docIds = Array.isArray(value) ? value : [value];
              const docs = docIds
                .map(getDocument)
                .filter((item) => item !== undefined)
                .map((doc) => (isPrivateDocumentDto(doc) ? doc.name : doc.shortname));

              return { label, value: docs.join(', ') };
            }

            return {
              label,
              value: (
                <AssetFieldValue asset={asset} fieldKey={fieldKey} step={index} sjDb={sjDb} isPdf />
              ),
            };
          });

        return {
          title: groups[index].title,
          fields,
        };
      });

      //#region Co-owners
      if (asset.coOwners?.length) {
        sections.push({
          title: 'Co-owners',
          itemGroups: [
            {
              items: asset.coOwners.map((coOwner) => ({
                fields: [
                  {
                    label: 'Co-owner',
                    value: getCoOwnerName(coOwner),
                  },
                  {
                    label: 'Share, %',
                    value: coOwner.coOwnerShare ?? EMPTY_TEXT,
                  },
                  {
                    label: 'Co-owners document',
                    value: getCoOwnerDocument(coOwner),
                  },
                ],
              })),
            },
          ],
        });
      }

      //#region Documents section
      if (assetDocuments.length) {
        sections.push({
          title: t('Documents'),
          itemGroups: [
            {
              items: assetDocuments.map((doc) => ({
                fields: [
                  {
                    label: 'Title',
                    value: doc.name ?? EMPTY_TEXT,
                  },
                  {
                    label: 'Category',
                    value: privateDocValues.category(doc.privateCategory) ?? EMPTY_TEXT,
                  },
                  {
                    label: 'End date',
                    value: doc.stopDate ? dates.formatLongDate(doc.stopDate) : EMPTY_TEXT,
                  },
                  {
                    label: 'Signatories',
                    value: renderSignatories({ doc, sjDb, emptyText: EMPTY_TEXT }),
                  },
                ],
              })),
            },
          ],
        });
      }

      return { sections, attachments };
    };

    return { getEntityData };
  }, [t, groups]);
};
